import { useState, useMemo } from 'react';
import { TaskCard, TaskDetailModal, TaskGroup, ResolveIssuesModal } from '../components/tasks';
import { MultiSelectFilter, NotificationToast } from '../components/common';
import { StatCard, DateRangeFilter } from '../components/evaluation';
import { AbsenceDrawer } from '../components/absence';
import { TRANSLATIONS } from '../config/translations';
import { getTodayISO } from '../utils/dateHelpers';

/**
 * Tasks view - list and filter all tasks with analytics for admins
 */
export function TasksView({ currentUser, language, tasks, locations, users, loading }) {
  const [selectedTask, setSelectedTask] = useState(null);
  const [resolvingTask, setResolvingTask] = useState(null);
  const [isAbsenceDrawerOpen, setIsAbsenceDrawerOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  // Calculate default date range (last 90 days)
  const getDefaultStartDate = () => {
    const d = new Date(getTodayISO() + 'T00:00:00');
    d.setDate(d.getDate() - 90);
    return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' });
  };

  const getDefaultEndDate = () => {
    return getTodayISO();
  };

  // Date range filters (affects stats AND task list)
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  // Single-select filters for user and location (affects stats AND task list)
  const [filterUserId, setFilterUserId] = useState(''); // Empty string = all users
  const [filterLocationId, setFilterLocationId] = useState(''); // Empty string = all locations

  // Multi-select status filter (affects task list ONLY, not stats)
  const [filterStatuses, setFilterStatuses] = useState([]);

  // How many completed tasks to render (Erledigt group is paginated, not cut off)
  const [visibleCompletedCount, setVisibleCompletedCount] = useState(10);

  const t = TRANSLATIONS[language];

  // Filter tasks for non-admin users (only show their assigned tasks)
  const userTasks = useMemo(() => {
    if (currentUser.role === 'admin') {
      return tasks;
    }
    return tasks.filter(task => task.assigneeId === currentUser.uid);
  }, [tasks, currentUser]);

  // Apply date range, user, and location filters
  // Date range ONLY filters completed tasks (for analytics)
  // Open tasks always show regardless of due date
  const dateAndUserFilteredTasks = useMemo(() => {
    return userTasks.filter(task => {
      const isOpenLike = task.status === 'open' || task.status === 'overdue';

      // Date range filter: ONLY apply to completed tasks
      if (!isOpenLike) {
        const cd = task.completedAt?.toDate?.();
        const taskCompletedDate = cd ? cd.toISOString().split('T')[0] : null;

        // For completed tasks, check if completedAt is in date range
        const isInDateRange = taskCompletedDate &&
                              taskCompletedDate >= startDate &&
                              taskCompletedDate <= endDate;

        if (!isInDateRange) return false;
      }
      // Open tasks: ignore date filter, always include

      // User filter (applies to all tasks)
      if (filterUserId && task.assigneeId !== filterUserId) return false;

      // Location filter (applies to all tasks)
      if (filterLocationId && task.locationId !== filterLocationId) return false;

      return true;
    });
  }, [userTasks, startDate, endDate, filterUserId, filterLocationId]);

  // Calculate analytics stats (ignores status filter, only uses date/user/location filters)
  const stats = useMemo(() => {
    const completedTasks = dateAndUserFilteredTasks.filter(
      task => task.status === 'completed' || task.status === 'completed-with-issues'
    );

    const totalCompleted = completedTasks.length;

    if (totalCompleted === 0) {
      return {
        totalCompleted: 0,
        percentDelayed: 0,
        percentOnTime: 0,
        percentWithIssues: 0,
        hasData: false
      };
    }

    const delayedTasks = completedTasks.filter(task => {
      const cd = task.completedAt?.toDate?.();
      const completedDate = cd ? cd.toISOString().split('T')[0] : null;
      return completedDate && completedDate > task.dueDate;
    });

    const onTimeTasks = completedTasks.filter(task => {
      const cd = task.completedAt?.toDate?.();
      const completedDate = cd ? cd.toISOString().split('T')[0] : null;
      return completedDate && completedDate <= task.dueDate;
    });

    const tasksWithIssues = completedTasks.filter(
      task => task.status === 'completed-with-issues'
    );

    return {
      totalCompleted,
      percentDelayed: Math.round((delayedTasks.length / totalCompleted) * 100),
      percentOnTime: Math.round((onTimeTasks.length / totalCompleted) * 100),
      percentWithIssues: Math.round((tasksWithIssues.length / totalCompleted) * 100),
      hasData: true
    };
  }, [dateAndUserFilteredTasks]);

  // Completed tasks across ALL time (ignores date range) — shown so admins
  // can tell filtering apart from data loss
  const totalCompletedAllTime = useMemo(() => {
    return userTasks.filter(
      task => task.status === 'completed' || task.status === 'completed-with-issues'
    ).length;
  }, [userTasks]);
  // Apply status filter to task list (does NOT affect stats)
  const filteredTasks = useMemo(() => {
    if (filterStatuses.length === 0) {
      return dateAndUserFilteredTasks;
    }

    return dateAndUserFilteredTasks.filter(task => filterStatuses.includes(task.status));
  }, [dateAndUserFilteredTasks, filterStatuses]);

  // Group filtered tasks for display
  const groupedTasks = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];

    return {
      overdue: filteredTasks.filter(t => (t.status === 'open' || t.status === 'overdue') && t.dueDate < today)
        .sort((a, b) => {
          // Sort by due date first (earliest first)
          const dateCompare = a.dueDate.localeCompare(b.dueDate);
          if (dateCompare !== 0) return dateCompare;
          // Then sort by title
          return (a.title || '').localeCompare(b.title || '');
        }),
      today: filteredTasks.filter(t => (t.status === 'open' || t.status === 'overdue') && t.dueDate === today)
        .sort((a, b) => (a.title || '').localeCompare(b.title || '')), // Sort alphabetically by title
      upcoming: filteredTasks.filter(t => (t.status === 'open' || t.status === 'overdue') && t.dueDate > today)
        .sort((a, b) => {
          // Sort by due date first (earliest first)
          const dateCompare = a.dueDate.localeCompare(b.dueDate);
          if (dateCompare !== 0) return dateCompare;
          // Then sort by title
          return (a.title || '').localeCompare(b.title || '');
        }),
      completed: filteredTasks.filter(t => t.status === 'completed' || t.status === 'completed-with-issues')
        .sort((a, b) => {
          // Sort completed-with-issues to top
          if (a.status === 'completed-with-issues' && b.status !== 'completed-with-issues') return -1;
          if (a.status !== 'completed-with-issues' && b.status === 'completed-with-issues') return 1;
          // Then sort by completion date (newest first)
          return b.completedAt?.seconds - a.completedAt?.seconds;
        })
    };
  }, [filteredTasks]);

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className="tasks-view">
      <div className="view-header">
        <h2>{t.tasks}</h2>
        {currentUser.role === 'admin' && (
          <div className="header-actions" style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn-secondary"
              onClick={() => setIsAbsenceDrawerOpen(true)}
            >
              <i className="fas fa-plane-departure"></i> Abwesenheit
            </button>
            <button
              className="btn-primary"
              onClick={() => window.location.hash = '#templates'}
            >
              <i className="fas fa-layer-group"></i> Vorlagen verwalten
            </button>
          </div>
        )}
      </div>

      {/* Analytics Stats - Admin Only */}
      {currentUser.role === 'admin' && (
        <div className="analytics-section">
          {/* Date Range and User/Location Filters */}
          <div className="analytics-filters">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              language={language}
            />
            <p className="date-range-hint">
              Erledigte seit {startDate} — {totalCompletedAllTime} insgesamt im Archiv.
              <button
                className="btn-link"
                onClick={() => { setStartDate('2020-01-01'); setEndDate(getTodayISO()); }}
              >
                Gesamtverlauf anzeigen
              </button>
            </p>

            <div className="filter-row">
              <div className="form-group">
                <label>{t.filterByUser}</label>
                <select
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  className="filter-select"
                >
                  <option value="">{t.allUsers}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t.filterByLocation}</label>
                <select
                  value={filterLocationId}
                  onChange={(e) => setFilterLocationId(e.target.value)}
                  className="filter-select"
                >
                  <option value="">{t.allLocations}</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <StatCard
              label={t.totalCompleted}
              value={stats.totalCompleted}
              isPercentage={false}
              icon="fas fa-check-circle"
              colorClass="stat-primary"
            />
            <StatCard
              label={t.percentOnTime}
              value={stats.percentOnTime}
              isPercentage={true}
              icon="fas fa-clock"
              colorClass="stat-success"
            />
            <StatCard
              label={t.percentDelayed}
              value={stats.percentDelayed}
              isPercentage={true}
              icon="fas fa-exclamation-triangle"
              colorClass="stat-warning"
            />
            <StatCard
              label={t.percentWithIssues}
              value={stats.percentWithIssues}
              isPercentage={true}
              icon="fas fa-tools"
              colorClass="stat-danger"
            />
          </div>

          {!stats.hasData && (
            <p className="no-stats-data">
              <i className="fas fa-info-circle"></i> {t.noDataForFilters}
            </p>
          )}
        </div>
      )}

      {/* Status Filter - Affects Task List Only */}
      <div className="task-list-filters">
        <h3>{t.filterByStatus}</h3>
        <MultiSelectFilter
          label="Status"
          items={[
            { id: 'open', name: 'Offen' },
            { id: 'overdue', name: 'Überfällig' },
            { id: 'completed', name: 'Erledigt' },
            { id: 'completed-with-issues', name: '(Teil-)Erledigt' }
          ]}
          selectedIds={filterStatuses}
          onApply={setFilterStatuses}
          getItemLabel={(item) => item.name}
          getItemId={(item) => item.id}
          pluralLabel="Status"
        />
      </div>

      {/* Task Groups */}
      <div className="task-groups">
        {groupedTasks.overdue.length > 0 && (
          <div className="task-group task-group-overdue">
            <h3><i className="fas fa-exclamation-triangle"></i> Überfällig ({groupedTasks.overdue.length})</h3>
            <div className="task-cards">
              {groupedTasks.overdue.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  locations={locations}
                  users={users}
                  onSelect={() => setSelectedTask(task)}
                />
              ))}
            </div>
          </div>
        )}

        {groupedTasks.today.length > 0 && (
          <div className="task-group task-group-today">
            <h3><i className="fas fa-calendar-day"></i> Heute fällig ({groupedTasks.today.length})</h3>
            <div className="task-cards">
              {groupedTasks.today.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  locations={locations}
                  users={users}
                  onSelect={() => setSelectedTask(task)}
                />
              ))}
            </div>
          </div>
        )}

        {groupedTasks.upcoming.length > 0 && (
          <div className="task-group task-group-upcoming">
            <h3><i className="fas fa-calendar-alt"></i> Anstehend ({groupedTasks.upcoming.length})</h3>
            <div className="task-cards">
              {groupedTasks.upcoming.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  locations={locations}
                  users={users}
                  onSelect={() => setSelectedTask(task)}
                />
              ))}
            </div>
          </div>
        )}

        {groupedTasks.completed.length > 0 && (
          <div className="task-group task-group-completed">
            <h3><i className="fas fa-check-circle"></i> Erledigt ({groupedTasks.completed.length})</h3>
            <div className="task-cards">
              {groupedTasks.completed.slice(0, visibleCompletedCount).map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  locations={locations}
                  users={users}
                  onSelect={() => setSelectedTask(task)}
                />
              ))}
            </div>
            {groupedTasks.completed.length > visibleCompletedCount && (
              <div className="show-more-row">
                <button
                  className="btn-secondary"
                  onClick={() => setVisibleCompletedCount(c => c + 25)}
                >
                  Weitere anzeigen ({groupedTasks.completed.length - visibleCompletedCount} übrig)
                </button>
                <button
                  className="btn-link"
                  onClick={() => setVisibleCompletedCount(groupedTasks.completed.length)}
                >
                  Alle anzeigen
                </button>
              </div>
            )}
          </div>
        )}

        {filteredTasks.length === 0 && (
          <p className="no-data">Keine Aufgaben gefunden</p>
        )}
      </div>

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          currentUser={currentUser}
          locations={locations}
          users={users}
          onClose={() => setSelectedTask(null)}
          onComplete={() => {
            setSelectedTask(null);
          }}
          onResolve={currentUser.role === 'admin' && selectedTask.status === 'completed-with-issues'
            ? () => {
                setResolvingTask(selectedTask);
                setSelectedTask(null);
              }
            : null}
          language={language}
        />
      )}

      {resolvingTask && (
        <ResolveIssuesModal
          task={resolvingTask}
          onClose={() => setResolvingTask(null)}
          onSuccess={() => {
            setResolvingTask(null);
          }}
        />
      )}

      {/* Render the Drawer component, which includes the backdrop class for visibility handling */}
      <AbsenceDrawer
        isOpen={isAbsenceDrawerOpen}
        onClose={() => setIsAbsenceDrawerOpen(false)}
        users={users}
        onSuccess={(msg) => setNotification({ message: msg, type: 'success' })}
        onError={(msg) => setNotification({ message: msg, type: 'error' })}
      />

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
